'use client'

import { decodeSafeEntities, stripAllTags } from '@/lib/sanitise-strings'
import { clearUrlProtocol } from '@/lib/utils/url'

import Link from 'next/link'
import { useContext } from 'react'

import { useHits } from 'react-instantsearch'
import Image from 'next/image'
import { generateImageKitURL } from '@/lib/generate-imagekit-url'
import { ConvertContext } from '@/app/providers'

import {
  trackBusinessProfileViewed,
  trackBusinessSocialViewed,
  trackBusinessWebsiteViewed,
  trackBusinessContactViewed,
} from '@/lib/convert/conversions/business-directory'

export default function BusinessDirectoryGrid() {
  const { items } = useHits()
  const { sdkContext } = useContext(ConvertContext)

  const handleProfileViewed = () => {
    trackBusinessProfileViewed(sdkContext)
  }

  const handleSocialViewed = () => {
    trackBusinessSocialViewed(sdkContext)
  }

  const handleWebsiteViewed = () => {
    trackBusinessWebsiteViewed(sdkContext)
  }

  const handleContactViewed = () => {
    trackBusinessContactViewed(sdkContext)
  }

  return (
    <div className="bg-theme py-l theme--primary-50">
      <div className="container">
        <ul className="ms:gr grid grid-cols-4 gap-s l:grid-cols-3 m:grid-cols-2 xs:grid-cols-1">
          {items.map((item) => {
            const hasSocialMedia =
              item?.social_media?.linkedin ||
              item?.social_media?.instagram ||
              item?.social_media?.twitter ||
              item?.social_media?.facebook ||
              false

            return (
              <li
                key={item.objectID}
                className="flex flex-col rounded-md border border-border bg-white p-m @container/grid-item ms:p-s">
                {item?.business_info?.logo_image && item?.permalink ? (
                  <div className="mb-s h-[85px] max-w-[70%]">
                    <Link href={item.permalink} onClick={handleProfileViewed}>
                      <Image
                        src={generateImageKitURL(item.business_info.logo_image, {
                          height: '240',
                          cropMode: 'pad_resize',
                          focus: 'center',
                        })}
                        height={240}
                        width={240}
                        alt={item.business_info.logo_image_alt}
                        className="block h-[85px] w-auto max-w-[min(170px,100%)] object-contain"
                      />
                    </Link>
                  </div>
                ) : null}

                <span className="mb-xxs">
                  {item.post_title && item.permalink ? (
                    <Link
                      href={item.permalink}
                      onClick={handleProfileViewed}
                      className="h5 xl:h6 transition-color mb-0 !line-clamp-3 block overflow-hidden text-ellipsis duration-200 ease-in-out hover:text-primary">
                      {decodeSafeEntities(item.post_title)}
                    </Link>
                  ) : null}
                </span>

                {item?.introduction ? (
                  <p className="mb-s hidden text-17 @[240px]/grid-item:!line-clamp-3 @[240px]/grid-item:block">
                    {stripAllTags(item.introduction)}
                  </p>
                ) : null}

                <ul className="mb-s text-black">
                  {item.business_info.telephone ? (
                    <li>
                      <Link
                        href={'tel:' + item.business_info.telephone}
                        onClick={handleContactViewed}
                        className="inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap break-all text-primary duration-300 hover:text-black">
                        {item.business_info.telephone}
                      </Link>
                    </li>
                  ) : null}

                  {item.business_info.email ? (
                    <li>
                      <Link
                        href={'mailto:' + item.business_info.email}
                        onClick={handleContactViewed}
                        className="inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap break-all text-primary duration-300 hover:text-black">
                        {item.business_info.email}
                      </Link>
                    </li>
                  ) : null}

                  {item.business_info.website ? (
                    <li>
                      <Link
                        href={item.business_info.website}
                        onClick={handleWebsiteViewed}
                        className="inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap break-all text-primary duration-300 hover:text-black"
                        target="_blank"
                        rel="noopener noreferrer">
                        {clearUrlProtocol(item.business_info.website)}
                      </Link>
                    </li>
                  ) : null}
                </ul>

                {hasSocialMedia ? (
                  <div className="mb-m flex items-center gap-xxs">
                    <div className="flex w-full flex-col gap-s">
                      <div className="flex gap-xs">
                        {item?.social_media?.linkedin ? (
                          <Link
                            href={item.social_media.linkedin}
                            target="_blank"
                            onClick={handleSocialViewed}
                            className="hover:text-primary">
                            <svg className="h-24 w-24" aria-hidden="true">
                              <use href="/img/sprite.svg#icon-linkedin"></use>
                            </svg>
                          </Link>
                        ) : null}

                        {item?.social_media?.instagram ? (
                          <Link
                            href={item.social_media.instagram}
                            target="_blank"
                            onClick={handleSocialViewed}
                            className="hover:text-primary">
                            <svg className="h-24 w-24" aria-hidden="true">
                              <use href="/img/sprite.svg#icon-instagram"></use>
                            </svg>
                          </Link>
                        ) : null}

                        {item?.social_media?.twitter ? (
                          <Link
                            href={item.social_media.twitter}
                            target="_blank"
                            onClick={handleSocialViewed}
                            className="hover:text-primary">
                            <svg className="h-24 w-24" aria-hidden="true">
                              <use href="/img/sprite.svg#icon-x"></use>
                            </svg>
                          </Link>
                        ) : null}

                        {item?.social_media?.facebook ? (
                          <Link
                            href={item.social_media.facebook}
                            target="_blank"
                            onClick={handleSocialViewed}
                            className="hover:text-primary">
                            <svg className="h-24 w-24" aria-hidden="true">
                              <use href="/img/sprite.svg#icon-facebook"></use>
                            </svg>
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}

                <Link
                  href={item.permalink}
                  onClick={handleProfileViewed}
                  className="btn btn--outline btn--small mt-auto w-full self-start @[400px]/grid-item:w-auto">
                  View Profile
                </Link>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
